import { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button,
  Badge, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Alert,
} from 'reactstrap';
import { Plus, Edit2, Trash2, UserCheck } from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

const VOICE_TONES    = [{ id: 'formal',    label: 'Formal' }, { id: 'informal', label: 'Informal' }];
const TALKATIVENESS  = [{ id: 'direto', label: 'Direto' }, { id: 'equilibrado', label: 'Equilibrado' }, { id: 'falante', label: 'Falante' }];

const EMPTY_FORM = { name: '', company_name: '', city: '', voice_tone: 'informal', talkativeness: 'equilibrado', products_services: '' };

function RadioGroup({ options, value, onChange }) {
  return (
    <div className='d-flex gap-2 flex-wrap'>
      {options.map(o => {
        const sel = value === o.id;
        return (
          <label key={o.id} onClick={() => onChange(o.id)} className='rounded' style={{
            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 14px',
            border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
            background: sel ? '#f0eeff' : '#fff', color: '#212529',
          }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 8,
              border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
              background: sel ? '#7366ff' : '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
            </span>
            <span className='small fw-medium'>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Agentes() {
  const { user } = useProspectAuth();
  const isAdmin   = user?.role === 'admin';

  const [agents, setAgents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [deleteId, setDeleteId] = useState(null);

  async function fetchAgents() {
    try {
      const { data } = await api.get('/agents');
      setAgents(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  const agentNameRef = useRef();

  useEffect(() => { fetchAgents(); }, []);
  useEffect(() => { if (modal) setTimeout(() => agentNameRef.current?.focus(), 100); }, [modal]);

  function openNew() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setError('');
    setModal(true);
  }

  function openEdit(agent) {
    setForm({
      name: agent.name || '',
      company_name: agent.company_name || '',
      city: agent.city || '',
      voice_tone: agent.voice_tone || 'informal',
      talkativeness: agent.talkativeness || 'equilibrado',
      products_services: agent.products_services || '',
    });
    setEditId(agent.id);
    setError('');
    setModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Nome do agente é obrigatório.'); return; }
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/agents/${editId}`, form);
        setAgents(prev => prev.map(a => a.id === editId ? data : a));
      } else {
        const { data } = await api.post('/agents', form);
        setAgents(prev => [...prev, data]);
      }
      setModal(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Não foi possível salvar o agente');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    try {
      await api.delete(`/agents/${deleteId}`);
      setAgents(prev => prev.filter(a => a.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      alertErro('Erro ao excluir', e.response?.data?.error || 'Não foi possível excluir o agente');
    }
  }

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  return (
    <>
      <Breadcrumbs mainTitle='Agentes' parent='Topo Prospect' title='Agentes' />
      <Container fluid>

        {isAdmin && (
          <div className='alert alert-secondary d-flex align-items-center gap-2 mb-4' style={{ fontSize: '0.85rem' }}>
            <UserCheck size={16} /> Modo auditoria — visualização apenas.
          </div>
        )}

        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div>
            <h4 className='mb-0 fw-bold'>Agentes de IA</h4>
            <p className='text-muted small mb-0'>Personalize a inteligência que responde seus leads</p>
          </div>
          {!isAdmin && (
            <Button color='primary' size='sm' onClick={openNew}>
              <Plus size={14} className='me-1' /> Novo agente
            </Button>
          )}
        </div>

        {loading ? (
          <p className='text-muted'>Carregando...</p>
        ) : agents.length === 0 ? (
          <Card>
            <CardBody className='text-center py-5'>
              <UserCheck size={40} className='text-muted mb-3' />
              <p className='text-muted'>Nenhum agente cadastrado.</p>
              {!isAdmin && (
                <Button color='primary' onClick={openNew}>
                  <Plus size={14} className='me-1' /> Criar primeiro agente
                </Button>
              )}
            </CardBody>
          </Card>
        ) : (
          <Row>
            {agents.map(a => (
              <Col md={4} key={a.id} className='mb-4'>
                <Card className='h-100'>
                  <CardBody>
                    <div className='d-flex align-items-start justify-content-between mb-3'>
                      <div className='d-flex align-items-center gap-3'>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#7366ff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                          {a.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className='mb-0 fw-semibold'>{a.name}</p>
                          <p className='mb-0 small text-muted'>{a.company_name || '—'}</p>
                        </div>
                      </div>
                      <Badge color={a.status === 'active' ? 'success' : 'secondary'}>
                        {a.status === 'active' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <p className='small text-muted mb-1'><strong>Cidade:</strong> {a.city || '—'}</p>
                    <p className='small text-muted mb-1'>
                      <strong>Tom:</strong> {a.voice_tone === 'formal' ? 'Formal' : 'Informal'} ·{' '}
                      <strong>Perfil:</strong> {a.talkativeness === 'direto' ? 'Direto' : a.talkativeness === 'falante' ? 'Falante' : 'Equilibrado'}
                    </p>
                    {a.products_services && (
                      <p className='small text-muted mb-0 mt-2' style={{ whiteSpace: 'pre-wrap' }}>
                        {a.products_services.slice(0, 100)}{a.products_services.length > 100 ? '…' : ''}
                      </p>
                    )}
                    {!isAdmin && (
                      <div className='d-flex gap-2 mt-3 pt-3 border-top'>
                        <Button color='light' size='sm' onClick={() => openEdit(a)}>
                          <Edit2 size={13} className='me-1' /> Editar
                        </Button>
                        <Button color='light' size='sm' className='text-danger' onClick={() => setDeleteId(a.id)}>
                          <Trash2 size={13} className='me-1' /> Excluir
                        </Button>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

      </Container>

      {/* Modal edição */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size='lg'>
        <ModalHeader toggle={() => setModal(false)}>
          {editId ? 'Editar agente' : 'Novo agente'}
        </ModalHeader>
        <ModalBody>
          {error && <Alert color='danger' className='small'>{error}</Alert>}
          <Form>
            <FormGroup>
              <Label className='fw-medium small'>Nome do agente *</Label>
              <Input innerRef={agentNameRef} value={form.name} onChange={e => f('name', e.target.value)} placeholder='Ex: Nayara' />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className='fw-medium small'>Nome da empresa</Label>
                  <Input value={form.company_name} onChange={e => f('company_name', e.target.value)} placeholder='Ex: Agência Topo 360' />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className='fw-medium small'>Cidade / Região</Label>
                  <Input value={form.city} onChange={e => f('city', e.target.value)} placeholder='Ex: Palmas - TO' />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label className='fw-medium small'>Tom de voz</Label>
              <RadioGroup options={VOICE_TONES} value={form.voice_tone} onChange={v => f('voice_tone', v)} />
            </FormGroup>
            <FormGroup>
              <Label className='fw-medium small'>Personalidade</Label>
              <RadioGroup options={TALKATIVENESS} value={form.talkativeness} onChange={v => f('talkativeness', v)} />
            </FormGroup>
            <FormGroup>
              <Label className='fw-medium small'>Produtos e serviços oferecidos</Label>
              <Input type='textarea' rows={4} value={form.products_services}
                onChange={e => f('products_services', e.target.value)}
                placeholder='Descreva os principais produtos/serviços que o agente deve promover...' />
            </FormGroup>
          </Form>
          <div className='p-3 rounded mt-2' style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <p className='mb-0 small text-muted' style={{ fontSize: '0.78rem' }}>
              🔒 <strong>Skill SDR Outbound</strong> — base profissional de prospecção, não editável.
              Garante qualidade nas abordagens.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={saving} onClick={handleSave}>
            {saving ? 'Salvando...' : 'Salvar agente'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal confirmação exclusão */}
      <Modal isOpen={!!deleteId} toggle={() => setDeleteId(null)}>
        <ModalHeader toggle={() => setDeleteId(null)}>Excluir agente?</ModalHeader>
        <ModalBody>
          <p className='text-muted small'>Esta ação não pode ser desfeita. Campanhas vinculadas a este agente perderão a referência.</p>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button color='danger' onClick={handleDelete}>Excluir</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
